import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../BlocksElement';
import type { AriaLiveValue, SkeletonSizeOption } from './skeleton.model';
import styles from './skeleton.styles';

/**
 * @slot default - displays light dom content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart loader-text - The component's loader text.
 */
export class BlocksSkeleton extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Style size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**sm** renders a skeleton smaller in height than the default</li>
   * <li>**lg** renders a skeleton larger in height than the default</li>
   * <li>**xl** renders a skeleton larger in height than the `lg` size</li>
   * <li>**xxl** renders a skeleton larger in height than the `xl` size</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: SkeletonSizeOption;

  /**
   * Width property
   * 1) By default, the width is set to 100%. This property overrides the default for customization.
   */
  @property({ type: String })
  width: string | undefined;

  /**
   * aria-label property. To be used for accessibility
   */
  @property({ type: String })
  arialabel = 'loading';

  /**
   * The ARIA live region politeness level for the loader.
   *
   * @type {string}
   * @default "assertive"
   */
  @property({ type: String })
  arialive: AriaLiveValue = 'assertive';

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('width') && this.width) {
      this.style.setProperty('--cat-skeleton-loader-width', this.width);
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-skeleton', {
      'cat-c-skeleton--sm': this.size === 'sm',
      'cat-c-skeleton--lg': this.size === 'lg',
      'cat-c-skeleton--xl': this.size === 'xl',
      'cat-c-skeleton--xxl': this.size === 'xxl'
    });

    return html`
      <div part="base" aria-live=${this.arialive} class="${componentClassNames}" aria-label=${ifDefined(this.arialabel)} role="alert">
        <span part="loader-text" class="cat-c-skeleton__loader-text"> ${this.arialabel}</span>
        <slot part="part-default"></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-skeleton') === undefined) {
  customElements.define('cat-skeleton', BlocksSkeleton);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-skeleton': BlocksSkeleton;
  }
}
